import { Card, Heading, IconButton, LuIcon, Menu, Separator, Text, toast } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, UNLABELLED_ASSET_LABEL, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import {
	APIError,
	selectAssetById,
	selectAssetTypeById,
	selectProcedureById,
	selectProcedureInitiativeById,
	selectProcedureStepsOfProcedureType,
	selectProcedureTypeById,
} from "@overmap-ai/core"
import { useOpenState } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ProcedurePdfExportOptionsDialog } from "../../components/Dialogs/ProcedurePdfExportOptionsDialog"
import { ProcedureTypeDescriptionDialog } from "../../components/Dialogs/ProcedureTypeDescriptionDialog"
import { AssetTypeIcon } from "../../components/Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../../components/Icons/ProcedureTypeIcon"
import { AssetIndexLabel } from "../../components/IndexLabels/AssetIndexLabel"
import { ProcedureIndexLabel } from "../../components/IndexLabels/ProcedureIndexLabel"
import { ProcedureInitiativeIndexLabel } from "../../components/IndexLabels/ProcedureInitiativeIndexLabel"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { PROCEDURE_STEP_COMPLETION_STATUSES } from "../../constants"
import { interactiveTextCva } from "../../cva"
import { ProcedureStepCompletionStatus } from "../../enums/procedures"
import { useProcedureCompletionData } from "../../hooks/procedures"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { procedureStepCompletionStatusData } from "../../mappings/procedures"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import type { ProcedurePdfExportOptions } from "../../typings/reports"
import { formatCompactDate } from "../../utils/date"
import { ProcedureDetailProcedure } from "./ProcedureDetailProcedure"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/procedures/$procedureId")

export const ProcedureDetailsView = memo(() => {
	const { procedureId } = routeApi.useParams()

	const procedure = useAppSelector(selectProcedureById(procedureId))

	if (!procedure) {
		throw new Error(`Procedure with id ${procedureId} does not exist`)
	}

	const procedureType = useAppSelector(selectProcedureTypeById(procedure.procedure_type))
	const asset = useAppSelector(selectAssetById(procedure.asset))
	const assetType = useAppSelector(selectAssetTypeById(asset?.asset_type ?? ""))
	const procedureInitiative = useAppSelector(selectProcedureInitiativeById(procedure.procedure_initiative ?? ""))

	if (!procedureType || !asset || !assetType) {
		throw new Error(`Procedure dependencies not found for procedure ${procedure.uuid}`)
	}

	const navigate = useNavigate()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isBasicUser = useUserOrganizationAccess(OrganizationAccessLevel.BASIC)
	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedure.procedure_type))
	const { completionStatusCounts } = useProcedureCompletionData(procedure)

	const completedSteps = completionStatusCounts[ProcedureStepCompletionStatus.COMPLETE]

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportPdf = useCallback(
		(options: ProcedurePdfExportOptions) => {
			void sdk.procedures.exportPdf(procedure.uuid, options).catch((e) => {
				toast.error({
					title: "Failed to download PDF.",
					description: e instanceof APIError ? e.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[procedure.uuid],
	)

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete procedure",
			description: "Are you sure you want to delete this procedure? This action cannot be undone.",
			onAction: async () => {
				await navigate({
					from: "/$organizationId/$projectId/procedures/$procedureId",
					to: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
					params: {
						procedureTypeId: procedure.procedure_type,
					},
				})

				void sdk.procedures.delete(procedure.uuid)
			},
		})
	}, [navigate, openConfirmDeleteDialog, procedure.procedure_type, procedure.uuid])

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex items-start justify-between gap-2">
					<Heading size="xl" className="flex min-w-0 items-center gap-2">
						<ProcedureTypeIcon procedureTypeId={procedureType.uuid} />
						<Text className="truncate">{procedureType.name}</Text>
						<ProcedureIndexLabel procedureId={procedure.uuid} accentColor="base" />
						<ProcedureTypeDescriptionDialog procedureType={procedureType} size="sm">
							<IconButton variant="ghost" accentColor="base" size="sm">
								<LuIcon icon={modelIconMapping.info} />
							</IconButton>
						</ProcedureTypeDescriptionDialog>
					</Heading>

					<div className="flex items-center gap-2">
						<Menu.Root align="end">
							<Menu.ClickTrigger>
								<IconButton variant="ghost" size="sm" accentColor="base">
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onClick={openPdfExportOptionsDialog}>
									<LuIcon icon={modelIconMapping.pdf} />
									Export to PDF
								</Menu.Item>
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
									<Menu.Separator />
									<Menu.Item onSelect={handleDelete}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</OrganizationAccessRequired>
							</Menu.Content>
						</Menu.Root>
					</div>
				</div>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<div className="flex flex-col gap-4">
							<Card className="flex grow flex-col gap-4" size="sm">
								<div className="flex flex-col gap-1">
									<Text accentColor="base" size="sm">
										Asset
									</Text>
									<Text accentColor="base" size="sm">
										<AssetTypeIcon assetTypeId={asset.asset_type} className="align-sub" />
										&nbsp;
										<Link
											className={cx("min-w-0", interactiveTextCva())}
											from="/$organizationId/$projectId/procedures/$procedureId"
											to="/$organizationId/$projectId/assets/$assetId"
											params={(prev) => ({ ...prev, assetId: asset.uuid })}
										>
											<AssetIndexLabel assetId={asset.uuid} highContrast />
											&nbsp;
											<Text className="truncate">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
										</Link>
									</Text>
								</div>

								<div className="flex flex-col gap-1">
									<Text accentColor="base" size="sm">
										Procedure
									</Text>
									<Text accentColor="base" size="sm">
										<ProcedureTypeIcon procedureTypeId={procedureType.uuid} className="align-sub" />
										&nbsp;
										<Link
											className={cx("min-w-0", interactiveTextCva())}
											from="/$organizationId/$projectId/procedures/$procedureId"
											to="/$organizationId/$projectId/procedure-types/$procedureTypeId"
											params={(prev) => ({ ...prev, procedureTypeId: procedureType.uuid })}
										>
											<Text className="truncate" highContrast>
												{procedureType.name}
											</Text>
										</Link>
									</Text>
								</div>

								<div className="flex flex-col gap-1">
									<Text accentColor="base" size="sm">
										Initiative
									</Text>
									<Text size="sm" accentColor="base">
										{procedureInitiative ? (
											<>
												<ProcedureTypeIcon
													procedureTypeId={procedureInitiative.procedure_type}
													className="align-sub"
												/>
												&nbsp;
												<Link
													className={cx("min-w-0", interactiveTextCva())}
													from="/$organizationId/$projectId/procedures/$procedureId"
													to="/$organizationId/$projectId/initiatives/$procedureInitiativeId"
													params={(prev) => ({
														...prev,
														procedureInitiativeId: procedureInitiative.uuid,
													})}
												>
													<ProcedureInitiativeIndexLabel
														procedureInitiativeId={procedureInitiative.uuid}
														size="sm"
														highContrast
													/>
												</Link>
											</>
										) : (
											<Text className="truncate" highContrast>
												None
											</Text>
										)}
									</Text>
								</div>
							</Card>
						</div>

						<Card className="flex flex-col gap-4" size="sm">
							<div className="flex items-center justify-between gap-2">
								<Text size="sm">Step completion</Text>
								<Text size="xs" accentColor="base">
									{completedSteps}/{procedureSteps.length}
								</Text>
							</div>

							<div className="flex flex-col gap-2">
								{PROCEDURE_STEP_COMPLETION_STATUSES.toReversed().map((status) => {
									const { color, icon, label } = procedureStepCompletionStatusData[status]

									return (
										<div key={status} className="flex items-center justify-between gap-2">
											<Text size="sm" accentColor="base" className="flex items-center gap-2">
												<LuIcon icon={icon} color={color} />
												{label}
											</Text>
											<Text size="sm">{completionStatusCounts[status]}</Text>
										</div>
									)
								})}
							</div>

							<Separator size="full" />

							<div className="flex items-center justify-between gap-2">
								<Text size="sm" accentColor="base">
									Started
								</Text>
								<Text size="sm">{formatCompactDate(procedure.submitted_at)}</Text>
							</div>
						</Card>
					</div>

					<ProcedureDetailProcedure procedure={procedure} readonly={!isBasicUser} />
				</div>
			</div>
			<ProcedurePdfExportOptionsDialog
				key={procedureType.uuid}
				procedureTypeId={procedureType.uuid}
				onSubmitOptions={handleExportPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</ViewContainer>
	)
})
ProcedureDetailsView.displayName = "ProcedureDetailsView"
